import * as fs from 'firebase/firestore'
import { isPreviewing, PreviewBlocked } from './preview'

/**
 * Firestore's write functions, with one rule added: nothing is written while a
 * "View as…" preview is on (see ./preview.ts). Rejects rather than throws, so
 * a caller's own `.catch` or try/await shows the message like any other
 * failed save.
 *
 * Import writes from here, never from 'firebase/firestore' directly —
 * tests/writes.test.ts fails the build if anything does, because a write that
 * skips this is a write a demo can make for real.
 */

const blocked = () => Promise.reject(new PreviewBlocked())

export const setDoc = ((...a: Parameters<typeof fs.setDoc>) =>
  isPreviewing() ? blocked() : fs.setDoc(...a)) as typeof fs.setDoc

export const updateDoc = ((...a: Parameters<typeof fs.updateDoc>) =>
  isPreviewing() ? blocked() : (fs.updateDoc as (...x: unknown[]) => Promise<void>)(...a)) as typeof fs.updateDoc

export const addDoc = ((...a: Parameters<typeof fs.addDoc>) =>
  isPreviewing() ? blocked() : fs.addDoc(...a)) as typeof fs.addDoc

export const deleteDoc = ((...a: Parameters<typeof fs.deleteDoc>) =>
  isPreviewing() ? blocked() : fs.deleteDoc(...a)) as typeof fs.deleteDoc

/** A batch whose commit refuses while previewing. Building it up is harmless. */
export const writeBatch = ((...a: Parameters<typeof fs.writeBatch>) => {
  const b = fs.writeBatch(...a)
  const commit = b.commit.bind(b)
  b.commit = () => (isPreviewing() ? blocked() : commit())
  return b
}) as typeof fs.writeBatch
